import { useEffect, useState, useMemo } from "react";
import { api, API, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import GalleryManager from "@/components/GalleryManager";
import CoachStatsManager from "@/components/CoachStatsManager";
import MembersManager from "@/components/MembersManager";
import ScheduleManager from "@/components/ScheduleManager";
import WeeklyScheduleManager from "@/components/WeeklyScheduleManager";
import SiteConfiguration from "@/components/SiteConfiguration";
import Avatar from "@/components/Avatar";
import { highestBadge } from "@/components/Badges";
import { Search, ChevronLeft, ChevronRight, LayoutDashboard, Users, Calendar, User, Settings, Clock } from "lucide-react";

const PLAN_NAMES = {
  coaching_1sem_monthly: "Coaching · 1/sem",
  coaching_2sem_monthly: "Coaching · 2/sem",
  libre_1sem_monthly: "Libre · 1/sem",
  libre_2sem_monthly: "Libre · 2/sem",
};

function fmtDate(iso) {
  return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Admin() {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [csvStart, setCsvStart] = useState("");
  const [csvEnd, setCsvEnd] = useState("");
  const [banner, setBanner] = useState({ enabled: false, text: "", cta_label: "", cta_href: "" });
  const [savingBanner, setSavingBanner] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Pagination and search states
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  // Function to reload current user (for profile updates)
  const reloadCurrentUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCurrentUser(data);
    } catch (e) {
      console.error("Failed to reload user:", e);
    }
  };

  useEffect(() => {
    // Load current user
    reloadCurrentUser();
    
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/subscribers").then((r) => setSubs(r.data));
    api.get("/admin/bookings").then((r) => setBookings(r.data));
    api.get("/banner").then((r) => setBanner({
      enabled: !!r.data.enabled,
      text: r.data.text || "",
      cta_label: r.data.cta_label || "",
      cta_href: r.data.cta_href || "",
    })).catch(() => {});
  }, []);

  // Filtered and sorted bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];
    
    // Sort by date (most recent first)
    filtered.sort((a, b) => new Date(b.slot_start) - new Date(a.slot_start));
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(b => {
        const name = (b.user_name || "").toLowerCase();
        const email = (b.user_email || "").toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }
    
    return filtered;
  }, [bookings, searchQuery]);

  // Paginated bookings
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredBookings.slice(startIndex, endIndex);
  }, [filteredBookings, currentPage, ITEMS_PER_PAGE]);

  const totalPages = Math.ceil(filteredBookings.length / ITEMS_PER_PAGE);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      const { data } = await api.put("/admin/banner", banner);
      setBanner({
        enabled: !!data.enabled,
        text: data.text || "",
        cta_label: data.cta_label || "",
        cta_href: data.cta_href || "",
      });
      toast.success(banner.enabled ? "Bandeau publié" : "Bandeau enregistré (désactivé)");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSavingBanner(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/admin/reminders/send");
      toast.success(`Rappels envoyés : ${data.sent} · Échecs : ${data.failed}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSendingReminders(false);
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (csvStart) params.set("start", csvStart);
    if (csvEnd) params.set("end", csvEnd);
    const res = await fetch(`${API}/admin/bookings/export?${params.toString()}`, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    a.download = match ? match[1] : "loulou-reservations.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handlePurgePastBookings = async () => {
    try {
      const { data } = await api.delete("/admin/bookings/purge-past");
      toast.success(data.message || `${data.deleted_count} ancienne(s) réservation(s) supprimée(s)`);
      // Reload bookings
      const { data: updatedBookings } = await api.get("/admin/bookings");
      setBookings(updatedBookings);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Erreur lors de la purge");
    }
  };

  const handleResetAllBookings = async () => {
    try {
      const { data } = await api.delete("/admin/bookings/reset-all");
      toast.success(data.message || `${data.deleted_count} réservation(s) supprimée(s)`);
      // Reload bookings
      const { data: updatedBookings } = await api.get("/admin/bookings");
      setBookings(updatedBookings);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Erreur lors de la réinitialisation");
    }
  };

  const tabs = [
    { id: "dashboard", label: "Vue d'ensemble", icon: LayoutDashboard },
    { id: "members", label: "Membres", icon: Users },
    { id: "schedule", label: "Calendrier", icon: Calendar },
    { id: "weekly", label: "Semainier", icon: Clock },
    { id: "config", label: "Configuration", icon: Settings },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000000' }}>
      <Nav />
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-12" style={{ backgroundColor: '#000000' }}>
        {/* Header with Avatar */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Administration</p>
            <h1 className="font-display text-4xl font-light tracking-tight text-stone-900">
              {tabs.find(t => t.id === activeTab)?.label || "Dashboard"}
            </h1>
          </div>
          {currentUser && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-stone-900">{currentUser.name}</p>
                <p className="text-xs text-stone-500">{currentUser.email}</p>
              </div>
              <Avatar user={currentUser} size="md" />
            </div>
          )}
        </div>

        {/* Tabs Navigation */}
        <div className="mb-8 flex gap-2 overflow-x-auto border-b border-stone-200 pb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-stone-900 text-stone-900"
                    : "border-transparent text-stone-500 hover:text-stone-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "dashboard" && (
          <>
        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Membres", value: stats?.total_members ?? "…", tid: "stat-members" },
            { label: "Abonnements actifs", value: stats?.active_subscriptions ?? "…", tid: "stat-subs" },
            { label: "Séances à venir", value: stats?.upcoming_sessions ?? "…", tid: "stat-upcoming" },
            { label: "MRR estimé", value: stats ? `${stats.mrr} €` : "…", tid: "stat-mrr" },
          ].map((s) => (
            <div key={s.label} data-testid={s.tid} className="rounded-2xl border border-stone-200 bg-white p-8">
              <p className="text-xs uppercase tracking-[0.24em] text-stone-500">{s.label}</p>
              <p className="font-display mt-4 text-4xl font-light text-stone-900">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Banner + Reminders */}
        <div className="mt-16 grid gap-6 lg:grid-cols-5">
          <div className="rounded-2xl border border-stone-200 bg-white p-8 lg:col-span-3">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-light text-stone-900">Bandeau d'accueil</h2>
                <p className="mt-1 text-sm text-stone-500">Message affiché tout en haut de la landing. Idéal pour annoncer une offre du mois.</p>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="banner-enabled" className="text-xs uppercase tracking-[0.2em] text-stone-500">Actif</Label>
                <Switch
                  id="banner-enabled"
                  data-testid="banner-enabled"
                  checked={banner.enabled}
                  onCheckedChange={(v) => setBanner({ ...banner, enabled: v })}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="banner-text" className="text-xs uppercase tracking-[0.2em] text-stone-500">Message</Label>
                <Input
                  id="banner-text"
                  data-testid="banner-text"
                  value={banner.text}
                  onChange={(e) => setBanner({ ...banner, text: e.target.value })}
                  placeholder="Offre de rentrée : -20% sur le forfait Coaching 2 séances jusqu'au 30 septembre."
                  className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="banner-cta-label" className="text-xs uppercase tracking-[0.2em] text-stone-500">Libellé bouton (optionnel)</Label>
                  <Input
                    id="banner-cta-label"
                    data-testid="banner-cta-label"
                    value={banner.cta_label}
                    onChange={(e) => setBanner({ ...banner, cta_label: e.target.value })}
                    placeholder="Voir les forfaits"
                    className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                  />
                </div>
                <div>
                  <Label htmlFor="banner-cta-href" className="text-xs uppercase tracking-[0.2em] text-stone-500">Lien</Label>
                  <Input
                    id="banner-cta-href"
                    data-testid="banner-cta-href"
                    value={banner.cta_href}
                    onChange={(e) => setBanner({ ...banner, cta_href: e.target.value })}
                    placeholder="/#plans"
                    className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                  />
                </div>
              </div>
              <Button
                data-testid="banner-save"
                onClick={saveBanner}
                disabled={savingBanner}
                className="rounded-full bg-stone-900 px-6 hover:bg-stone-800"
              >
                {savingBanner ? "Enregistrement…" : "Enregistrer le bandeau"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-900 bg-stone-900 p-8 text-stone-50 lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.24em] text-stone-400">Rappels automatiques</p>
            <h3 className="font-display mt-3 text-2xl font-light">Mail J-1</h3>
            <p className="mt-3 text-sm text-stone-300">Un e-mail de rappel est envoyé automatiquement 24h avant chaque séance confirmée. Vous pouvez déclencher un envoi manuel :</p>
            <Button
              data-testid="reminders-send"
              onClick={sendReminders}
              disabled={sendingReminders}
              className="mt-6 w-full rounded-full bg-[#8B7D6B] py-5 hover:bg-[#7A6C5B]"
            >
              {sendingReminders ? "Envoi…" : "Envoyer les rappels maintenant"}
            </Button>
          </div>
        </div>

        {/* Gallery Manager */}
        <div className="mt-16">
          <GalleryManager />
        </div>

        {/* Coach stats */}
        <div className="mt-8">
          <CoachStatsManager />
        </div>

        {/* Subscribers */}
        <div className="mt-16">
          <h2 className="font-display mb-6 text-2xl font-light text-stone-900">Inscrits</h2>
          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-100 text-left text-xs uppercase tracking-[0.2em] text-stone-500">
                <tr>
                  <th className="px-6 py-4 font-medium">Membre</th>
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Forfait</th>
                  <th className="px-6 py-4 font-medium">Essai</th>
                  <th className="px-6 py-4 font-medium">Réserv.</th>
                  <th className="px-6 py-4 font-medium">Inscription</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-stone-500" data-testid="subs-empty">Aucun inscrit pour le moment.</td></tr>
                ) : subs.map((u, i) => {
                  const badge = highestBadge(u.bookings_count || 0);
                  return (
                  <tr key={u.id} data-testid={`sub-row-${i}`} className={i > 0 ? "border-t border-stone-100" : ""}>
                    <td className="px-6 py-4 text-stone-900">
                      <div className="flex items-center gap-2">
                        <span>{u.name || "—"}</span>
                        {badge && (
                          <span
                            data-testid={`sub-badge-${i}`}
                            title={badge.blurb}
                            className="rounded-full bg-[#8B7D6B]/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.2em] text-[#8B7D6B]"
                          >
                            {badge.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-stone-600">{u.email}</td>
                    <td className="px-6 py-4">
                      {u.active_plan ? (
                        <span className="rounded-full bg-stone-100 px-3 py-1 text-xs text-stone-700">{PLAN_NAMES[u.active_plan] || u.active_plan}</span>
                      ) : (
                        <span className="text-xs text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-stone-500">{u.trial_used ? "Utilisé" : "Disponible"}</td>
                    <td className="px-6 py-4 text-stone-900">{u.bookings_count}</td>
                    <td className="px-6 py-4 text-xs text-stone-500">{u.created_at ? fmtDate(u.created_at) : "—"}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bookings */}
        <div className="mt-16">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-light text-stone-900">Réservations</h2>
              <p className="mt-1 text-sm text-stone-500">
                {filteredBookings.length} réservation{filteredBookings.length > 1 ? 's' : ''} · Les plus récentes en premier
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              {/* Search bar */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Rechercher par nom ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full pl-10 pr-4 sm:w-64 rounded-full border-stone-300 bg-white text-stone-900 placeholder:text-stone-400 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500">Du</label>
                <input data-testid="csv-start" type="date" value={csvStart} onChange={(e) => setCsvStart(e.target.value)} className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-900" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500">Au</label>
                <input data-testid="csv-end" type="date" value={csvEnd} onChange={(e) => setCsvEnd(e.target.value)} className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-900" />
              </div>
              <Button data-testid="csv-export" onClick={exportCsv} className="h-10 rounded-full bg-stone-900 px-5 text-sm hover:bg-stone-800">
                Exporter CSV
              </Button>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-100 text-left text-xs uppercase tracking-[0.2em] text-stone-500">
                <tr>
                  <th className="px-6 py-4 font-medium">Créneau</th>
                  <th className="px-6 py-4 font-medium">Client</th>
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Forfait choisi</th>
                  <th className="px-6 py-4 font-medium">Type</th>
                  <th className="px-6 py-4 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBookings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-stone-500" data-testid="admin-bookings-empty">
                      {searchQuery ? "Aucune réservation trouvée pour cette recherche." : "Aucune réservation."}
                    </td>
                  </tr>
                ) : paginatedBookings.map((b, i) => (
                  <tr key={b.id} data-testid={`admin-booking-row-${i}`} className={i > 0 ? "border-t border-stone-100" : ""}>
                    <td data-testid={`admin-booking-slot-${i}`} className="px-6 py-4 capitalize text-stone-900">{fmtDate(b.slot_start)}</td>
                    <td data-testid={`admin-booking-name-${i}`} className="px-6 py-4 text-stone-900">{b.user_name || "—"}</td>
                    <td data-testid={`admin-booking-email-${i}`} className="px-6 py-4 text-stone-600">{b.user_email}</td>
                    <td data-testid={`admin-booking-plan-${i}`} className="px-6 py-4">
                      {b.user_plan ? (
                        <span className="rounded-full bg-stone-100 px-3 py-1 text-xs text-stone-700">{b.user_plan_name || PLAN_NAMES[b.user_plan]}</span>
                      ) : b.is_trial ? (
                        <span className="rounded-full bg-[#8B7D6B]/10 px-3 py-1 text-xs text-[#8B7D6B]">Séance d&apos;essai</span>
                      ) : (
                        <span className="text-xs text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">{b.is_trial ? <span className="text-[#8B7D6B]">Essai</span> : "Abonnement"}</td>
                    <td className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500">{b.status === "cancelled" ? "Annulée" : "Confirmée"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-stone-600">
                Page {currentPage} sur {totalPages} · Affichage de {paginatedBookings.length} sur {filteredBookings.length}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-full"
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
        </>
        )}

        {/* Members Tab */}
        {activeTab === "members" && (
          <div>
            <MembersManager />
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === "schedule" && (
          <div className="space-y-6">
            {/* Cleanup Actions */}
            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-medium text-stone-900">Nettoyage du calendrier</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => {
                    if (confirm("⚠️ Êtes-vous sûr de vouloir supprimer toutes les réservations passées ?\n\nCette action est irréversible.")) {
                      handlePurgePastBookings();
                    }
                  }}
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  🧹 Purger les anciennes séances
                </Button>
                <Button
                  onClick={() => {
                    if (confirm("🚨 ATTENTION : Voulez-vous vraiment supprimer TOUTES les réservations du calendrier ?\n\nCette action est irréversible et supprimera toutes les séances (passées et futures).")) {
                      handleResetAllBookings();
                    }
                  }}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  🗑️ Réinitialiser toutes les séances
                </Button>
              </div>
              <p className="mt-3 text-xs text-stone-500">
                <strong>Purger :</strong> Supprime uniquement les réservations passées.{" "}
                <strong>Réinitialiser :</strong> Vide complètement le calendrier (utile pour les tests).
              </p>
            </div>

            <ScheduleManager />
          </div>
        )}

        {/* Configuration Tab */}

        {/* Weekly Schedule Tab */}
        {activeTab === "weekly" && (
          <div>
            <WeeklyScheduleManager />
          </div>
        )}

        {activeTab === "config" && (
          <div>
            <SiteConfiguration />
          </div>
        )}
      </div>
    </div>
  );
}
